// src/app/shared/utils/toast.service.ts
// Portal Dewelopera - Toast Service
// Simplified wrapper around PrimeNG MessageService

import {Injectable} from '@angular/core';
import {MessageService} from 'primeng/api';

export const APP_GLOBAL_TOAST_KEY = 'app-global-toast';

type ToastSeverity = 'success' | 'error' | 'warn' | 'info';

interface ManagedToast {
  id: number;
  severity: ToastSeverity;
  summary: string;
  detail?: string;
  life: number;
  timeoutId?: ReturnType<typeof setTimeout>;
}

@Injectable({providedIn: 'root'})
export class ToastService {
  private static readonly MAX_VISIBLE_TOASTS = 3;
  private static readonly SUCCESS_LIFE_MS = 5000;
  private static readonly ERROR_LIFE_MS = 7000;
  private static readonly WARNING_LIFE_MS = 6000;
  private static readonly INFO_LIFE_MS = 5000;

  private nextToastId = 0;
  private readonly activeToasts: ManagedToast[] = [];

  constructor(private messageService: MessageService) {
  }

  /**
   * Show success message
   */
  showSuccess(summary: string, detail?: string) {
    this.addManagedToast('success', summary, detail, ToastService.SUCCESS_LIFE_MS);
  }

  /**
   * Show error message
   */
  showError(summary: string, detail?: string) {
    this.addManagedToast('error', summary, detail, ToastService.ERROR_LIFE_MS);
  }

  /**
   * Show warning message
   */
  showWarning(summary: string, detail?: string) {
    this.addManagedToast('warn', summary, detail, ToastService.WARNING_LIFE_MS);
  }

  /**
   * Show info message
   */
  showInfo(summary: string, detail?: string) {
    this.addManagedToast('info', summary, detail, ToastService.INFO_LIFE_MS);
  }

  /**
   * Show custom message with full control
   */
  show(severity: ToastSeverity, summary: string, detail?: string, life?: number) {
    this.addManagedToast(severity, summary, detail, life || ToastService.INFO_LIFE_MS);
  }

  /**
   * Clear all messages
   */
  clear() {
    this.clearManagedToasts();
    this.messageService.clear(APP_GLOBAL_TOAST_KEY);
  }

  /**
   * Clear specific message by key
   */
  clearByKey(key: string) {
    if (key === APP_GLOBAL_TOAST_KEY) {
      this.clearManagedToasts();
    }
    this.messageService.clear(key);
  }

  // Legacy methods for backward compatibility
  success(detail: string, summary = 'Sukces') {
    this.showSuccess(summary, detail);
  }

  error(detail: string, summary = 'Błąd') {
    this.showError(summary, detail);
  }

  warn(detail: string, summary = 'Ostrzeżenie') {
    this.showWarning(summary, detail);
  }

  info(detail: string, summary = 'Informacja') {
    this.showInfo(summary, detail);
  }

  private addManagedToast(
    severity: ToastSeverity,
    summary: string,
    detail: string | undefined,
    life: number
  ): void {
    const toast: ManagedToast = {
      id: this.nextToastId++,
      severity,
      summary,
      detail,
      life,
    };

    if (this.activeToasts.length >= ToastService.MAX_VISIBLE_TOASTS) {
      const [oldestToast] = this.activeToasts.splice(0, 1);
      this.clearToastTimer(oldestToast);
      this.activeToasts.push(toast);
      this.resetManagedToastTimers();
      this.messageService.clear(APP_GLOBAL_TOAST_KEY);
      this.messageService.addAll(this.activeToasts.map(({severity, summary, detail, life}) => ({
        key: APP_GLOBAL_TOAST_KEY,
        severity,
        summary,
        detail,
        life,
      })));
      return;
    }

    this.activeToasts.push(toast);
    this.scheduleToastRemoval(toast);
    this.messageService.add({key: APP_GLOBAL_TOAST_KEY, severity, summary, detail, life});
  }

  private scheduleToastRemoval(toast: ManagedToast): void {
    toast.timeoutId = setTimeout(() => {
      const toastIndex = this.activeToasts.findIndex((activeToast) => activeToast.id === toast.id);
      if (toastIndex >= 0) {
        this.activeToasts.splice(toastIndex, 1);
      }
    }, toast.life);
  }

  private resetManagedToastTimers(): void {
    this.activeToasts.forEach((toast) => {
      this.clearToastTimer(toast);
      this.scheduleToastRemoval(toast);
    });
  }

  private clearManagedToasts(): void {
    this.activeToasts.forEach((toast) => this.clearToastTimer(toast));
    this.activeToasts.length = 0;
  }

  private clearToastTimer(toast: ManagedToast | undefined): void {
    if (toast?.timeoutId) {
      clearTimeout(toast.timeoutId);
      toast.timeoutId = undefined;
    }
  }
}
